import { Badge, Descriptions, Flex } from 'antd';
import {
  ActionButton,
  CustomSegmented,
  DetailLayout,
  DropdownItem,
  ErrorAlertWithDrawer,
  GetApiErrorMessage,
  isLocked,
  LockedBy,
  ObjectHeader,
  ObjectType,
  Padding,
  PopconfirmDelete,
  Spinner,
  Stack,
  useCheckLockAndNavigate,
  useIsAdmin,
  useMutateRole,
  useMutationErrors,
  useRoleQuery,
  useShowLockData,
  useAlertContext,
  InfoIconUrl,
  useQueryParams,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './RoleDetails.translations.json';
import { RoleBreadcrumbs } from './RoleBreadcrumbs';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { RoleAccessGroupTable } from './RoleAccessGroupTable';
import { useCallback, useMemo } from 'react';
import { RolePermissionTable } from './RolePermissionTable';
import { useNavigateTo } from '../../paths/useNavigateTo';
import { path } from '../../paths';

const { addTranslations, useTranslation, t } = i18nNsFactory('role-details');
addTranslations(translations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t('alert.error.code.' + code, meta?.args && { ...meta.args });

export const RoleDetails = () => {
  const { t } = useTranslation();

  const params = useParams<{ id: string }>();
  const roleId = Number(params.id);
  if (isNaN(roleId)) throw new Error('Role ID not provided');

  const { data: role } = useRoleQuery({
    id: roleId,
  });

  const [{ segment: segmentParam }, setQueryParams] = useQueryParams<{
    segment: 'permissions' | 'accessGroups';
  }>();
  const segment =
    segmentParam === 'accessGroups' ? segmentParam : 'permissions';
  const segmentOptions = useMemo(
    () => [
      {
        value: 'permissions',
        label: (
          <Flex gap="small" align="center">
            {t('segment-option.permissions')}{' '}
            <Badge count={role?.permissions.length} />
          </Flex>
        ),
      } as const,
      {
        value: 'accessGroups',
        label: (
          <Flex gap="small" align="center">
            {t('segment-option.accessGroups')}{' '}
            <Badge count={role?.accessGroups.length} />
          </Flex>
        ),
      } as const,
    ],
    [t, role],
  );

  const { lockData, refetchLockData } = useShowLockData(
    ObjectType.Role,
    roleId,
  );
  const navigateToIndex = useNavigateTo(path.roleDirectory);
  const navigateToRoleClone = useNavigateTo(path.roleClone, { id: roleId });
  const navigateToRoleEdit = useNavigateTo(path.roleEdit, { id: roleId });
  const checkLockAndNavigateToEdit = useCheckLockAndNavigate(
    refetchLockData,
    lockData,
    () => navigateToRoleEdit({ segment }),
  );

  const { mutate } = useMutateRole();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);
  const { setSuccess } = useAlertContext();

  const handleDelete = useCallback(() => {
    clearMutationErrors();
    mutate(
      {
        id: roleId,
      },
      {
        onSuccess: () => {
          navigateToIndex();
          setSuccess(t('alert.success.delete'));
        },
        onError: ({ response }) => {
          setMutationErrors(response?.data.errors || []);
        },
      },
    );
  }, [
    clearMutationErrors,
    mutate,
    roleId,
    navigateToIndex,
    setSuccess,
    t,
    setMutationErrors,
  ]);

  const hasPermissionToManage = useIsAdmin();

  if (!role) {
    return <Spinner />;
  }

  return (
    <DetailLayout
      infoIconUrl={InfoIconUrl.ROLE_DETAILS}
      type="detail"
      breadCrumb={<RoleBreadcrumbs data={role} />}
      headerConfig={
        hasPermissionToManage
          ? {
              actions: (
                <ActionButton
                  disabled={isLocked(lockData)}
                  type="mainAction"
                  layoutType="detail"
                  onClick={() => checkLockAndNavigateToEdit()}
                />
              ),
              dropdownMenu: {
                items: [
                  {
                    key: 'clone',
                    label: (
                      <DropdownItem onClick={() => navigateToRoleClone()}>
                        {t('button.clone')}
                      </DropdownItem>
                    ),
                  },
                  {
                    disabled: isLocked(lockData),
                    key: 'delete',
                    label: (
                      <PopconfirmDelete
                        stopClickPropagation
                        onDelete={handleDelete}
                        genitiveModelName={t('delete.genitive')}
                      />
                    ),
                  },
                ],
              },
            }
          : undefined
      }
      side={{
        header: (
          <Padding>
            <ObjectHeader lowerText={role.name} upperPlaceholder="" />
          </Padding>
        ),
        content: (
          <Padding>
            <Descriptions
              column={1}
              items={[
                {
                  key: 'description',
                  label: t('description.label'),
                  children: role.description,
                },
              ]}
            />
          </Padding>
        ),
        footer: (
          <LockedBy id={role.id} entity={role} objectType={ObjectType.Role} />
        ),
      }}
      alert={<ErrorAlertWithDrawer validationMessagesMap={mutationErrors} />}
    >
      <Padding>
        <ContentWrapper>
          <CustomSegmented
            value={segment}
            onChange={(segment) => setQueryParams({ segment })}
            options={segmentOptions}
          />
          {segment === 'permissions' && (
            <RolePermissionTable permissions={role.permissions} />
          )}
          {segment === 'accessGroups' && (
            <RoleAccessGroupTable accessGroups={role.accessGroups} />
          )}
        </ContentWrapper>
      </Padding>
    </DetailLayout>
  );
};

const ContentWrapper = styled(Stack)`
  width: 100%;
  max-width: 100em;
  height: 100%;
  align-self: center;
  align-items: center;
  margin: 0 auto;
`;
